import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Layout from "../../../components/layouts/Layout";
import { User } from "../../../types/userTypes";
import { Visibility, VisibilityOff, ContentCopy } from "@mui/icons-material";
import ConfirmDialog from "../../../components/dialog/ConfirmationDialog";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import SuperAdminUserService from './../../../service/superadmin/userService';

const ProfilePage = () => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [formData, setFormData] = useState({
        name: "",
        username: "",
        email: "",
        address: "",
        phone: "",
        country_code: "+1",
        role: "",
        status: "1",
        password: "",
    });

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    const userId = userData?.id;

    const fetchUserProfile = async () => {
        try {
            const user = await SuperAdminUserService.getUserById(userId);

            // Extract country code from the phone number
            let extractedCountryCode = "+1"; // Default
            let extractedPhone = user.phone;

            // Example assumption: If phone starts with +XX, extract it
            const phoneMatch = user.phone.match(/^(\+\d{1,4})\s*(.*)/);
            if (phoneMatch) {
                extractedCountryCode = phoneMatch[1]; // Get the country code
                extractedPhone = phoneMatch[2]; // Get the rest of the phone number
            }

            setUser(user);
            setFormData({
                ...user,
                country_code: extractedCountryCode, // Set extracted country code
                phone: extractedPhone, // Set phone without country code
                password: "", // Keep password empty
            });
        } catch (error) {
            toast.error("Failed to load user details");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        if (!text) {
            toast.error("No password to copy!");
            return;
        }
        navigator.clipboard.writeText(text);
        toast.info("Copied to clipboard!");
    };

    useEffect(() => {
        fetchUserProfile();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const copyPassword = () => {
        navigator.clipboard.writeText(formData.password);
        toast.info("Password copied to clipboard!");
    };

    const handleProfileUpdate = async () => {
        setShowConfirmDialog(false);
        await handleSubmit();
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault(); // Prevent form submission if called from a button

        setLoading(true);
        try {
            const { password, ...dataToSend } = formData; // Exclude password from profile update

            await SuperAdminUserService.updateUser(userId, dataToSend); // Send only necessary data
            toast.success("Profile updated successfully!");
            setNewPassword("");
            setConfirmPassword("");
            await fetchUserProfile(); // Refresh user profile after update

        } catch (error) {
            toast.error("Failed to update profile");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePassword = async () => {
        if (newPassword !== confirmPassword) {
            toast.error("Passwords do not match!");
            return;
        }
        setLoading(true);
        try {
            await SuperAdminUserService.updateUserPassword(userId, newPassword);
            toast.success("Password updated successfully!");
            setShowPasswordModal(false);
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            toast.error("Failed to update password");
        } finally {
            setLoading(false);
        }
    };

    const generatePassword = () => {
        const generatedPassword = Math.random().toString(36).slice(-10);
        setNewPassword(generatedPassword);
        setConfirmPassword(generatedPassword);
    };


    if (loading)
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="spinner-border animate-spin w-8 h-8 border-4 border-gray-600 rounded-full"></div>
            </div>
        );

    return (
        <Layout>
            <div className="max-w-3xl mx-auto mt-10 p-6 bg-white shadow-lg rounded-lg">
                <h2 className="text-2xl font-semibold text-gray-700 mb-5">Profile</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Name</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Username</label>
                            <input
                                type="text"
                                disabled
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                                            focus:ring-indigo-500 focus:border-indigo-500 
                                            bg-gray-100 text-gray-500 cursor-not-allowed"
                            />
                        </div>
                    </div> */}

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Country Code</label>
                            <select
                                name="country_code"
                                value={formData.country_code}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                            >
                                <option value="+1">+1 (US)</option>
                                <option value="+44">+44 (UK)</option>
                                <option value="+33">+33 (France)</option>
                                <option value="+49">+49 (Germany)</option>
                            </select>
                        </div>
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Phone</label>
                            <input
                                type="number"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Address</label>
                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                            required
                        />
                    </div>

                    <div className="flex justify-between">
                        <button
                            type="button"
                            onClick={() => setShowConfirmDialog(true)}
                            className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800"
                        >
                            Update Profile
                        </button>

                        <button
                            type="button"
                            onClick={() => setShowPasswordModal(true)}
                            className="focus:outline-none text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
                        >
                            Update Password
                        </button>
                    </div>

                </form>
            </div>

            <ConfirmDialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={handleProfileUpdate}
                title="Update Account Information"
                message="Are you sure you want to update account information?"
            />

            {showPasswordModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-[600px] max-w-2xl">
                        <h2 className="text-xl font-semibold text-gray-700 mb-4">Update Password</h2>


                        <TextField
                            fullWidth
                            margin="normal"
                            label="New Password"
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                        <IconButton onClick={() => copyToClipboard(newPassword)} edge="end">
                                            <ContentCopy />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            margin="normal"
                            label="Confirm Password"
                            type={showConfirmNewPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}>
                                            {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                        {/* <IconButton onClick={() => copyToClipboard(confirmPassword)} edge="end">
                                            <ContentCopy />
                                        </IconButton> */}
                                    </InputAdornment>
                                ),
                            }}
                        />


                        {/* Buttons */}
                        <div className="flex justify-between mt-4">
                            <button
                                onClick={generatePassword}
                                className="text-white bg-yellow-500 hover:bg-yellow-600 px-4 py-2 rounded-lg transition duration-200"
                            >
                                Generate Password
                            </button>
                            <button
                                onClick={handleUpdatePassword}
                                className="text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg transition duration-200"
                            >
                                Update Password
                            </button>
                            <button
                                onClick={() => setShowPasswordModal(false)}
                                className="text-gray-700 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-200 transition duration-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}


        </Layout>
    );
};

export default ProfilePage;
